"use client";

import { useEffect, useRef } from "react";
import type { DiscoveryBrowserState } from "@/lib/discovery/webmcp";
import { captureDiscoveryEvent } from "@/lib/discovery/analytics";

/** Ordinary browsers do not download the tool implementation or fetch any data. */
export function useDiscoveryWebMcp(state: DiscoveryBrowserState) {
  const latest = useRef(state);
  useEffect(() => { latest.current = state; }, [state]);

  useEffect(() => {
    if (!("modelContext" in document) && !("modelContext" in navigator)) return;
    let disposed = false;
    let cleanup: (() => void) | undefined;
    void import("@/lib/discovery/webmcp").then(({ getNativeModelContext, createDiscoveryTools, registerDiscoveryTools }) => {
      if (disposed) return;
      const context = getNativeModelContext(document, navigator);
      if (context) cleanup = registerDiscoveryTools(context, createDiscoveryTools(() => latest.current).map((tool) => ({
        ...tool,
        execute: (input: unknown) => {
          // Mark real tool usage before its callback can request playback.
          captureDiscoveryEvent("discovery_webmcp_tool_called", { tool: tool.name }, true);
          try {
            const result = tool.execute(input);
            const data = result && typeof result === "object" ? result as Record<string, unknown> : {};
            captureDiscoveryEvent("discovery_webmcp_tool_completed", { tool: tool.name, success: !("error" in data),
              result_count: typeof data.total === "number" ? data.total : undefined });
            return result;
          } catch (error) {
            captureDiscoveryEvent("discovery_webmcp_tool_completed", { tool: tool.name, success: false });
            throw error;
          }
        },
      })));
    }).catch((error) => {
      // Keep experimental tooling optional while making local integration
      // failures visible to developers who have enabled the native capability.
      if (process.env.NODE_ENV === "development") console.warn("Unable to initialize finda.show WebMCP tools", error);
    });
    return () => { disposed = true; cleanup?.(); };
  }, []);
}
